import { Coordinate, Vms, VmsFault } from '_lib/api';
import { RootState, useAppDispatch } from '_store';
import applicationReducer from '_store/application/reducer';
import { DialogReturnTarget, DialogType } from '_store/application/types';
import devicesReducer from '_store/devices/devicesReducers';
import { RsuStatusCode } from '_store/devices/devicesTypes';
import { devicesSelector } from '_store/devices/selectors';
import { getRsuStatusFromDiagnostic } from '_store/devices/utils';
import { getIvimTitle, ivimStatuses } from '_store/ivim/utils';
import { pmvsSagas } from '_store/pmv/sagas';
import { vmsIvimsByIdSelector, vmsIvimsLoadingSelector } from '_store/pmv/selectors';
import { findDeviceByRsuRef, isVmsWorking } from '_store/pmv/utils';
import { useLocalization } from '@fluent/react';
import { CardContent, Grid, Typography } from '@material-ui/core';
import React, { FC, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { RsuStatus } from '../../../icons';
import { toLatLng } from '../../../routes/MapView/utils';
import { DotComponent } from '../../Event/DotComponent';
import { EventStatus } from '../../Event/EventStatus';
import styles from './styles';

export interface PmvCardDetailsProps {
  vms: Vms;
}

const showTextOrHyphen = (text: string | number | undefined) => text || '-';

const vmsReturnCoordinates = (vms: Vms): Coordinate | undefined => {
  if (!vms.latitude || !vms.longitude) return undefined;
  return [vms.longitude, vms.latitude];
};

const getPmvReturnTarget = (
  currentDialog: { type?: DialogType } | null | undefined,
  vms: Vms,
): DialogReturnTarget => ({
  type: currentDialog?.type === DialogType.SINGLE_PMV ? DialogType.SINGLE_PMV : DialogType.PMV_PATH,
  id: vms.id,
  coordinates: vmsReturnCoordinates(vms),
  zoom: 15,
});

const formatFaultLabel = (fault: VmsFault): string => {
  const parts = [fault.code];
  if (fault.description) parts.push(fault.description);
  if (fault.severity) parts.push(`(${fault.severity})`);
  return parts.join(' — ');
};

const PmvCardDetails: FC<PmvCardDetailsProps> = ({ vms }) => {
  const classes = styles();
  const { l10n } = useLocalization();
  const dispatch = useAppDispatch();
  const devices = useSelector(devicesSelector);
  const diagnostics = useSelector((state: RootState) => state.api.diagnostic);
  const mapRef = useSelector((state: RootState) => state.application.mapRef);
  const dialog = useSelector((state: RootState) => state.application.activeDialog);
  const relatedIvims = useSelector(vmsIvimsByIdSelector(vms.id));
  const ivimsLoading = useSelector(vmsIvimsLoadingSelector(vms.id));
  const working = isVmsWorking(vms);
  const faults = vms.status?.faults ?? [];

  const associatedRsus = useMemo(
    () =>
      (vms.enrichment?.rsus ?? []).map((rsuRef) => {
        const deviceMatch = findDeviceByRsuRef(rsuRef, devices);
        const device = deviceMatch?.id
          ? devices.find((d) => d.id === deviceMatch.id)
          : undefined;
        const diagnostic = device ? diagnostics[device.id] : undefined;
        const status =
          getRsuStatusFromDiagnostic(diagnostic) ??
          (device?.alive ? RsuStatusCode.UP : undefined);
        return {
          rsuRef,
          deviceId: device?.id,
          label: device?.name ?? rsuRef,
          status,
        };
      }),
    [devices, diagnostics, vms.enrichment?.rsus],
  );

  useEffect(() => {
    if (!working) return;
    dispatch(pmvsSagas.actions.getVmsIvim(vms.id));
  }, [dispatch, vms.id, working]);

  const openRsu = (deviceId: string) => {
    const device = devices.find((d) => d.id === deviceId);
    if (device?.location?.coordinates && mapRef) {
      mapRef.flyTo(toLatLng(device.location.coordinates), 15, { animate: true, duration: 1 });
    }

    const returnTo = getPmvReturnTarget(dialog, vms);

    dispatch(devicesReducer.actions.deviceRequest(deviceId));
    dispatch(
      applicationReducer.actions.infoSelected({
        type: DialogType.SINGLE_DEVICE,
        id: deviceId,
        returnTo,
      }),
    );
  };

  const openIvim = (ivimId: string) => {
    const ivim = relatedIvims.find((item) => item.id === ivimId);
    if (ivim?.referencePoint?.coordinates && mapRef) {
      mapRef.flyTo(toLatLng(ivim.referencePoint.coordinates), 15, { animate: true, duration: 1 });
    }

    dispatch(
      applicationReducer.actions.infoSelected({
        type: DialogType.IVIM,
        id: ivimId,
        returnTo: getPmvReturnTarget(dialog, vms),
      }),
    );
  };

  const faultList =
    faults.length === 0 ? (
      <Typography variant="body2" color="textSecondary">
        {l10n.getString('forms-pmv-faults-empty')}
      </Typography>
    ) : (
      <ul className={classes.faultList}>
        {faults.map((fault, index) => (
          <li key={`${fault.scope}-${fault.code}-${index}`}>
            <Typography variant="body2">
              <strong>{formatFaultLabel(fault)}</strong>
            </Typography>
            {fault.lastUpdateTime ? (
              <Typography variant="caption" color="textSecondary" display="block">
                {fault.lastUpdateTime}
              </Typography>
            ) : null}
          </li>
        ))}
      </ul>
    );

  return (
    <CardContent className={classes.gridVertical}>
      <Grid container spacing={1} className={classes.innerGrid}>
        <Grid item xs={12}>
          <Typography variant="caption" color="textSecondary">
            {l10n.getString('forms-pmv-detail-type')}
          </Typography>
          <Typography variant="body2">
            <strong>{showTextOrHyphen(vms.type)}</strong>
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="textSecondary">
            {l10n.getString('forms-pmv-detail-mounting')}
          </Typography>
          <Typography variant="body2">
            <strong>{showTextOrHyphen(vms.mounting)}</strong>
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="textSecondary">
            {l10n.getString('forms-pmv-detail-latitude')}
          </Typography>
          <Typography variant="body2">
            <strong>{showTextOrHyphen(vms.latitude)}</strong>
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="textSecondary">
            {l10n.getString('forms-pmv-detail-longitude')}
          </Typography>
          <Typography variant="body2">
            <strong>{showTextOrHyphen(vms.longitude)}</strong>
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="textSecondary">
            {l10n.getString('forms-pmv-detail-road-number')}
          </Typography>
          <Typography variant="body2">
            <strong>{showTextOrHyphen(vms.roadNumber)}</strong>
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="textSecondary">
            {l10n.getString('forms-pmv-detail-road-direction')}
          </Typography>
          <Typography variant="body2">
            <strong>{showTextOrHyphen(vms.roadDirection)}</strong>
          </Typography>
        </Grid>
        {vms.status?.updatedAt ? (
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              {l10n.getString('forms-pmv-detail-updated-at')}
            </Typography>
            <Typography variant="body2">
              <strong>{vms.status.updatedAt}</strong>
            </Typography>
          </Grid>
        ) : null}
      </Grid>

      <Typography variant="caption" color="textSecondary" display="block" className={classes.rsuSectionLabel}>
        {l10n.getString('forms-pmv-detail-rsus')}
      </Typography>
      {associatedRsus.length > 0 ? (
        <ul className={classes.rsuList}>
          {associatedRsus.map(({ rsuRef, deviceId, label, status }) => (
            <li key={rsuRef}>
              {deviceId ? (
                <button
                  type="button"
                  className={classes.rsuLink}
                  onClick={() => openRsu(deviceId)}
                >
                  <span className={classes.rsuStatusIcon}>
                    <RsuStatus status={status} />
                  </span>
                  <span className={classes.rsuLinkLabel}>{label}</span>
                </button>
              ) : (
                <span className={classes.rsuText}>{label}</span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <Typography variant="body2">
          <strong>-</strong>
        </Typography>
      )}

      {!working ? (
        <>
          <Typography variant="subtitle2" className={classes.ivimSectionTitle}>
            {l10n.getString('forms-pmv-vms-fault')}
          </Typography>
          {faultList}
        </>
      ) : (
        <>
          {faults.length > 0 ? (
            <>
              <Typography variant="subtitle2" className={classes.ivimSectionTitle}>
                {l10n.getString('forms-pmv-vms-fault')}
              </Typography>
              {faultList}
            </>
          ) : null}
          <Typography variant="subtitle2" className={classes.ivimSectionTitle}>
            {l10n.getString('forms-pmv-related-ivims')}
          </Typography>
          {ivimsLoading ? (
            <Typography variant="body2" color="textSecondary">
              {l10n.getString('forms-pmv-related-ivims-loading')}
            </Typography>
          ) : relatedIvims.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              {l10n.getString('forms-pmv-related-ivims-empty')}
            </Typography>
          ) : (
            <ul className={classes.ivimList}>
              {relatedIvims.map((ivim) => (
                <li key={ivim.id}>
                  <button
                    type="button"
                    className={classes.ivimLink}
                    onClick={() => openIvim(ivim.id)}
                  >
                    <DotComponent status={ivimStatuses(ivim) as EventStatus} />
                    <span>{getIvimTitle(ivim, l10n)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </CardContent>
  );
};

export default React.memo(PmvCardDetails);
